import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Compass, Home, Search } from 'lucide-react';

/**
 * Catch-all 404. Sets <meta name="robots" content="noindex"> and the
 * Prerender-style status hint so search crawlers don't index the URL or
 * count it as soft-404 / soft-redirect.
 *
 * We can't return a real HTTP 404 from a client-rendered SPA route, but
 * combining `noindex` + a 404 hint + an explicit "Page not found" body is
 * what Google's documentation tells SPA owners to do.
 */
export default function NotFound() {
    useEffect(() => {
        // Override the global index,follow with noindex for this view.
        let robots = document.head.querySelector('meta[name="robots"]');
        if (!robots) {
            robots = document.createElement('meta');
            robots.setAttribute('name', 'robots');
            document.head.appendChild(robots);
        }
        const original = robots.getAttribute('content');
        robots.setAttribute('content', 'noindex,follow');

        // Prerender-style status code hint (some crawlers honour it).
        let status = document.head.querySelector('meta[name="prerender-status-code"]');
        if (!status) {
            status = document.createElement('meta');
            status.setAttribute('name', 'prerender-status-code');
            document.head.appendChild(status);
        }
        status.setAttribute('content', '404');

        document.title = 'Page not found — Linguistic Quest';

        return () => {
            // Restore the site default on navigation away.
            if (original) robots.setAttribute('content', original);
            else robots.setAttribute('content', 'index,follow,max-image-preview:large');
            status.remove();
        };
    }, []);

    return (
        <main className="max-w-2xl mx-auto px-6 py-20 text-center" data-testid="page-not-found">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-lq-purple-100 text-lq-purple-700 mb-4">
                <Compass size={28} />
            </div>
            <h1 className="font-display text-4xl md:text-5xl font-semibold text-lq-ink-black">404 — Page not found</h1>
            <p className="mt-4 text-lg text-lq-ink-700">
                The link you followed may be broken, or the page may have been moved.
            </p>

            <div className="mt-10 grid sm:grid-cols-2 gap-4 text-left">
                <Link to="/" className="bg-white border border-lq-ink-100 rounded-2xl p-5 hover:border-lq-purple-300 hover:shadow transition-all" data-testid="not-found-home">
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-lq-purple-100 text-lq-purple-700 mb-2"><Home size={16} /></div>
                    <h2 className="font-display text-lg font-semibold text-lq-ink-900">Start at the homepage</h2>
                    <p className="text-sm text-lq-ink-700 mt-1">A quick tour of how families learn here.</p>
                </Link>
                <Link to="/languages" className="bg-white border border-lq-ink-100 rounded-2xl p-5 hover:border-lq-purple-300 hover:shadow transition-all" data-testid="not-found-languages">
                    <div className="inline-flex items-center justify-center w-9 h-9 rounded-lg bg-lq-peach-100 text-lq-peach-600 mb-2"><Search size={16} /></div>
                    <h2 className="font-display text-lg font-semibold text-lq-ink-900">Browse our languages</h2>
                    <p className="text-sm text-lq-ink-700 mt-1">Shona, Yoruba, Swahili and more.</p>
                </Link>
            </div>

            <p className="mt-10 text-sm text-lq-ink-500">
                Think this page should exist? Email{' '}
                <a href="mailto:hello@linguisticquest.com" className="text-lq-purple-700 hover:underline">hello@linguisticquest.com</a>.
            </p>
        </main>
    );
}
